import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ArrowRight, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MaskReveal } from "./Reveal";
import { FIRM, PRACTICE_AREAS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"];

const inputCls =
  "bg-transparent border-0 border-b border-white/25 rounded-none px-0 h-12 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-[#D4AF37] font-body";

export const Booking = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "General Consultation", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !date || !time) {
      toast.error("Please fill name, phone, email, date and time.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time,
      });
      setDone(true);
      toast.success("Consultation requested. We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", subject: "General Consultation", message: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="booking-section" className="bg-[#0A1128] bg-noise relative py-24 md:py-36">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="overline text-[#D4AF37] mb-6">/ Book Consultation</div>
          <MaskReveal as="h2"
            className="font-serif-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02] mb-8"
            lines={["Request a", "confidential", "consultation."]} />
          <p className="font-body text-white/60 text-base leading-relaxed max-w-md mb-10 font-light">
            Share your matter and preferred time. {FIRM.advocate}&rsquo;s office will confirm your appointment and, where relevant, add it to the calendar.
          </p>
          <div className="space-y-4 font-body text-white/70 text-sm">
            <p><span className="text-[#D4AF37]">Call</span>&nbsp;&nbsp;+91 {FIRM.mobile}</p>
            <p><span className="text-[#D4AF37]">Email</span>&nbsp;&nbsp;{FIRM.email}</p>
            <p><span className="text-[#D4AF37]">Office</span>&nbsp;&nbsp;{FIRM.address}</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              data-testid="booking-success"
              className="border border-[#D4AF37]/30 p-10 md:p-14 flex flex-col items-start">
              <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center mb-6">
                <Check className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif-display text-white text-3xl mb-3">Request received.</h3>
              <p className="font-body text-white/60 max-w-md mb-8">
                Thank you. The office has been notified and will reach out to confirm your consultation shortly.
              </p>
              <button data-testid="booking-another-btn" onClick={() => setDone(false)}
                className="text-[#D4AF37] link-underline">Book another consultation</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="md:col-span-1">
                <Label className="overline text-white/50">Full Name</Label>
                <Input data-testid="booking-name" value={form.name} onChange={set("name")}
                  className={inputCls} placeholder="Your name" aria-label="Full name" />
              </div>
              <div className="md:col-span-1">
                <Label className="overline text-white/50">Phone</Label>
                <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")}
                  className={inputCls} placeholder="Mobile number" aria-label="Phone" />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-white/50">Email</Label>
                <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")}
                  className={inputCls} placeholder="you@email.com" aria-label="Email" />
              </div>

              <div className="md:col-span-1">
                <Label className="overline text-white/50">Preferred Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="booking-date-trigger"
                      className={`${inputCls} w-full flex items-center justify-between text-left ${!date && "text-white/40"}`}>
                      {date ? format(date, "dd MMM yyyy") : "Select date"}
                      <CalendarIcon size={16} className="text-[#D4AF37]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="md:col-span-1">
                <Label className="overline text-white/50">Preferred Time</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="booking-time-trigger" className={`${inputCls} justify-between`} aria-label="Time">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`time-slot-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="overline text-white/50">Nature of Matter</Label>
                <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
                  <SelectTrigger data-testid="booking-subject-trigger" className={`${inputCls} justify-between`} aria-label="Matter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="General Consultation">General Consultation</SelectItem>
                    {PRACTICE_AREAS.map((p) => (
                      <SelectItem key={p.title} value={p.title}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="overline text-white/50">Message</Label>
                <Textarea data-testid="booking-message" value={form.message} onChange={set("message")}
                  className={`${inputCls} min-h-[90px] resize-none`} placeholder="Briefly describe your matter (optional)" aria-label="Message" />
              </div>

              <div className="md:col-span-2">
                <button type="submit" data-testid="booking-submit-btn" disabled={loading}
                  className="group w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0A1128] px-9 py-4 font-medium hover:bg-white transition-colors disabled:opacity-60">
                  {loading ? "Sending..." : "Request Consultation"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
